import { CourseTee } from '../types/Course';

import { generateFirestoreId } from './utils';
import { csvCell, downloadCsvBlob } from './utilsCsv';

// Build CSV header row based on hole count
function buildHeader(holeCount: number): string[] {
  const meta = ['Tee', 'Color', 'ComboColor', 'IsForMen', 'IsForWomen', 'MensRating', 'WomensRating', 'MensSlope', 'WomensSlope', 'MensUSGATeeSetRatingId', 'WomensUSGATeeSetRatingId', 'Type'];
  const holes = Array.from({ length: holeCount }, (_, i) => `H${i + 1}`);
  if (holeCount === 18) {
    return [...meta, ...holes.slice(0, 9), 'Out', ...holes.slice(9), 'In', 'Total'];
  }
  return [...meta, ...holes, 'Total'];
}

// Build hole value cells, inserting computed subtotals at Out/In/Total positions
function buildHoleRow(values: number[], holeCount: number): string[] {
  if (holeCount === 18) {
    const front = values.slice(0, 9);
    const back = values.slice(9);
    const outTotal = front.reduce((a, b) => a + b, 0);
    const inTotal = back.reduce((a, b) => a + b, 0);
    return [
      ...front.map(String),
      String(outTotal),
      ...back.map(String),
      String(inTotal),
      String(outTotal + inTotal),
    ];
  }
  const total = values.reduce((a, b) => a + b, 0);
  return [...values.map(String), String(total)];
}


export function exportTeesAsCSV(tees: CourseTee[], holeCount: number, courseName: string): void {
  const header = buildHeader(holeCount);
  const rows: string[][] = [header];

  for (const tee of tees) {
    const forMen = tee.isForMen !== false;
    const forWomen = tee.isForWomen !== false;
    const dataRows: [string, number[]][] = [
      ['Yards', tee.yardage],
      ['Par', tee.par],
      ...(forMen ? [['MensHcp', tee.mensStrokeIndex] as [string, number[]]] : []),
      ...(forWomen ? [['WomensHcp', tee.womensStrokeIndex] as [string, number[]]] : []),
    ];

    for (let i = 0; i < dataRows.length; i++) {
      const [type, values] = dataRows[i];
      const meta =
        i === 0
          ? [
              tee.name,
              tee.color ?? '',
              tee.combo ? (tee.color2 ?? '') : '',
              forMen ? 'TRUE' : 'FALSE',
              forWomen ? 'TRUE' : 'FALSE',
              String(tee.mensRating),
              String(tee.womensRating),
              String(tee.mensSlope),
              String(tee.womensSlope),
              tee.usgaMaleTeeSetRatingId != null ? String(tee.usgaMaleTeeSetRatingId) : '',
              tee.usgaFemaleTeeSetRatingId != null ? String(tee.usgaFemaleTeeSetRatingId) : '',
            ]
          : [tee.name, '', '', '', '', '', '', '', '', '', ''];
      rows.push([...meta, type, ...buildHoleRow(values, holeCount)]);
    }
  }

  const csvContent = rows.map((row) => row.map(csvCell).join(',')).join('\n');
  downloadCsvBlob(csvContent, `${courseName.replace(/[^a-z0-9]/gi, '_')}_tees.csv`);
}

// Parse CSV text into CourseTees. Returns tees and any non-fatal warnings.
export function parseTeesFromCSV(csvText: string): { tees: CourseTee[]; errors: string[] } {
  const errors: string[] = [];

  const parseRow = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    result.push(current.trim());
    return result;
  };

  const lines = csvText.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) {
    return { tees: [], errors: ['CSV must have a header row and at least one data row.'] };
  }

  const header = parseRow(lines[0]);
  const col = (name: string) => header.indexOf(name);

  const teeCol = col('Tee');
  const typeCol = col('Type');
  const colorCol = col('Color');
  const comboColorCol = col('ComboColor');
  const isForMenCol = col('IsForMen');
  const isForWomenCol = col('IsForWomen');
  const mensRatingCol = col('MensRating');
  const womensRatingCol = col('WomensRating');
  const mensSlopeCol = col('MensSlope');
  const womensSlopeCol = col('WomensSlope');
  // Optional — CSVs exported before these columns existed import with null ids. The legacy
  // GhinMaleTeeId / GhinFemaleTeeId headers are still read so older exports keep importing.
  const colEither = (name: string, legacyName: string) => (col(name) !== -1 ? col(name) : col(legacyName));
  const usgaMaleTeeIdCol = colEither('MensUSGATeeSetRatingId', 'GhinMaleTeeId');
  const usgaFemaleTeeIdCol = colEither('WomensUSGATeeSetRatingId', 'GhinFemaleTeeId');

  if (teeCol === -1 || typeCol === -1) {
    return { tees: [], errors: ['Invalid CSV: missing required columns (Tee, Type).'] };
  }

  // Determine hole count from header columns
  const holeCount = header.includes('H10') ? 18 : 9;
  const holeIndices: number[] = [];
  for (let h = 1; h <= holeCount; h++) {
    const idx = col(`H${h}`);
    if (idx === -1) {
      return { tees: [], errors: [`Invalid CSV: missing column H${h}.`] };
    }
    holeIndices.push(idx);
  }

  // Group rows by tee name, preserving order
  const teeOrder: string[] = [];
  const teeGroups = new Map<string, { row: string[]; type: string }[]>();

  for (let i = 1; i < lines.length; i++) {
    const row = parseRow(lines[i]);
    if (row.every((c) => !c)) continue;
    const teeName = row[teeCol];
    const type = row[typeCol];
    if (!teeName) continue;
    if (!teeGroups.has(teeName)) {
      teeGroups.set(teeName, []);
      teeOrder.push(teeName);
    }
    teeGroups.get(teeName)!.push({ row, type });
  }

  if (teeOrder.length === 0) {
    return { tees: [], errors: ['No tee data found in CSV.'] };
  }

  const getUsgaTeeSetRatingId = (row: string[], idx: number): number | null => {
    if (idx < 0) return null;
    const parsed = parseInt(row[idx], 10);
    return isNaN(parsed) ? null : parsed;
  };

  const getHoleValues = (row: string[] | undefined): number[] => {
    if (!row) return Array(holeCount).fill(0);
    return holeIndices.map((idx) => parseInt(row[idx], 10) || 0);
  };

  const tees: CourseTee[] = [];
  for (let sortOrder = 0; sortOrder < teeOrder.length; sortOrder++) {
    const teeName = teeOrder[sortOrder];
    const rows = teeGroups.get(teeName)!;

    const findRow = (type: string) =>
      rows.find((r) => r.type.toLowerCase() === type.toLowerCase());

    const metaRow = findRow('Yards')?.row ?? rows[0]?.row;
    if (!metaRow) continue;

    const comboColorVal = comboColorCol >= 0 ? (metaRow[comboColorCol] ?? '') : '';
    const isCombo = !!comboColorVal;

    const isForMen = isForMenCol >= 0 ? metaRow[isForMenCol]?.toUpperCase() !== 'FALSE' : undefined;
    const isForWomen = isForWomenCol >= 0 ? metaRow[isForWomenCol]?.toUpperCase() !== 'FALSE' : undefined;

    tees.push({
      itemId: generateFirestoreId(),
      name: teeName,
      color: colorCol >= 0 ? metaRow[colorCol] || undefined : undefined,
      combo: isCombo || undefined,
      color2: isCombo ? comboColorVal : undefined,
      ...(isForMen === false ? { isForMen: false } : {}),
      ...(isForWomen === false ? { isForWomen: false } : {}),
      mensRating: mensRatingCol >= 0 ? parseFloat(metaRow[mensRatingCol]) || 0 : 0,
      womensRating: womensRatingCol >= 0 ? parseFloat(metaRow[womensRatingCol]) || 0 : 0,
      mensSlope: mensSlopeCol >= 0 ? parseInt(metaRow[mensSlopeCol], 10) || 0 : 0,
      womensSlope: womensSlopeCol >= 0 ? parseInt(metaRow[womensSlopeCol], 10) || 0 : 0,
      yardage: getHoleValues(findRow('Yards')?.row),
      par: getHoleValues(findRow('Par')?.row),
      mensStrokeIndex: getHoleValues(findRow('MensHcp')?.row),
      womensStrokeIndex: getHoleValues(findRow('WomensHcp')?.row),
      sortOrder,
      usgaMaleTeeSetRatingId: getUsgaTeeSetRatingId(metaRow, usgaMaleTeeIdCol),
      usgaFemaleTeeSetRatingId: getUsgaTeeSetRatingId(metaRow, usgaFemaleTeeIdCol),
    });
  }

  return { tees, errors };
}
